// src/admin/components/common/CopyButton.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * A reusable copy-to-clipboard button.
 * 
 * @param {string} value - The text to copy to clipboard
 * @param {string} title - Tooltip text (default: "Copy to clipboard")
 * @param {string} variant - 'icon' (just icon) or 'text' (shows "Copy"/"Copied!")
 * @param {string} size - 'sm' or 'md'
 * @param {string} className - Additional CSS classes
 */
export default function CopyButton({
  value,
  title = 'Copy to clipboard',
  variant = 'icon',
  size = 'sm',
  className = '',
  onClick,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      if (onClick) onClick(e);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (variant === 'text') {
    return (
      <button
        type="button"
        className={classNames(
          'btn btn-outline-secondary',
          size === 'sm' ? 'btn-sm' : '',
          className
        )}
        onClick={handleCopy}
        title={title}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    );
  }

  // Icon variant (default)
  return (
    <button
      type="button"
      className={classNames(
        'btn btn-sm btn-outline-secondary py-0 px-1',
        className
      )}
      onClick={handleCopy}
      title={title}
      style={{ fontSize: size === 'sm' ? '0.75rem' : '0.875rem' }}
    >
      <i className={`bi ${copied ? 'bi-check' : 'bi-clipboard'}`} />
    </button>
  );
}

CopyButton.propTypes = {
  value: PropTypes.string.isRequired,
  title: PropTypes.string,
  variant: PropTypes.oneOf(['icon', 'text']),
  size: PropTypes.oneOf(['sm', 'md']),
  className: PropTypes.string,
  onClick: PropTypes.func,
};
